import { createClient, InteractiveAuth, MatrixClient } from 'matrix-js-sdk/src/matrix';
import { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import uuid4 from 'uuid4';

import useToast from 'contexts/ToastProvider/useToast';
import useNetworkErrorGuard, { NETWORK_GUARD_ERROR_MESSAGE } from 'hooks/useNetworkErrorGuard';
import { authLogger } from 'utils/logging/scopedLogger';
import { meedioMatrixFetchFn } from 'utils/matrixClientFetch';

import { InitialMatrixContextType } from './types';
import useStartMatrixClient from './useStartMatrixClient';

const useRegisterGuestMatrixAccount = (matrixContextValue: InitialMatrixContextType) => {
  const { t } = useTranslation();
  const authClient = useRef<MatrixClient>();
  const { pushToast } = useToast();
  const startMatrixClient = useStartMatrixClient(matrixContextValue);
  const { runWithNetworkGuard } = useNetworkErrorGuard();

  const register = useCallback(
    async (baseUrl: string) => {
      authClient.current = createClient({ baseUrl, fetchFn: meedioMatrixFetchFn });

      const interactiveAuth = new InteractiveAuth({
        matrixClient: authClient.current!,
        doRequest: (auth) =>
          runWithNetworkGuard(
            () =>
              authClient.current!.registerRequest({
                password: uuid4(),
                auth: auth || undefined,
              }),
            5
          ),
        stateUpdated: (nextStage, { error }) => {
          if (error) throw new Error(error);
          if (nextStage === 'm.login.dummy') interactiveAuth.submitAuthDict({ type: 'm.login.dummy' });
        },
        requestEmailToken: () => Promise.resolve({ sid: 'dummy' }),
      });

      const pushErrorToast = () => pushToast({ variant: 'error', title: t('errors.something_went_wrong') });

      const matrixClient = await interactiveAuth
        .attemptAuth()
        .then(async (authResponse) => {
          if (!authResponse.access_token || !authResponse.device_id) {
            authLogger.error('AuthResponse is invalid', authResponse);
            return pushErrorToast();
          }

          authLogger.info('Start guest matrix client');
          const matrixClient = await startMatrixClient({
            customUrl: baseUrl,
            accessToken: authResponse.access_token,
            deviceId: authResponse.device_id,
            userId: authResponse.user_id,
            isGuest: true,
          });

          return matrixClient;
        })
        .catch((error) => {
          authLogger.error('Error while attempting to register a guest matrix account', error);
          const hasNetworkError = error instanceof Error && error.message.includes(NETWORK_GUARD_ERROR_MESSAGE);
          if (!hasNetworkError) pushErrorToast();
        });

      return matrixClient;
    },
    [pushToast, runWithNetworkGuard, startMatrixClient, t]
  );

  return register;
};

export default useRegisterGuestMatrixAccount;
